import { KOPECKS_PER_LOYALTY_POINT } from '@/entities/loyalty/@x/order';
import { isMoney } from '@/shared/lib';
import {
  OrderPaymentMethod,
  type SettleOrderInput,
  type SettleOrderPaymentInput,
} from '@shared/api/graphql/gql/graphql';

import type { OrderCheckoutContextEntity } from '../model/types';

import { type OrderAttempt, OrderInputError } from './order-attempt';

/** Режим оплаты чекаута: `null` — метод ещё не выбран. */
export type OrderPaymentMode = 'card' | 'cash' | 'split' | 'link' | 'invoice' | null;

type AdvanceContext = Pick<OrderCheckoutContextEntity, 'advance_total' | 'overpayment_to_refund'>;

/**
 * Касса участвует только на своём шаге: карта, наличные, разделение или итоговый чек с зачётом
 * аванса. Оплата ссылкой вносит аванс без кассы и смены.
 */
export const orderUsesRegister = (
  mode: OrderPaymentMode,
  payableAmount: number,
  advanceAmount: number,
  finalReceiptRequired = false,
): boolean =>
  mode === 'card' ||
  mode === 'cash' ||
  mode === 'split' ||
  (payableAmount === 0 && advanceAmount > 0 && finalReceiptRequired);

/** Аванс, зачитываемый в этот заказ: переплата возвращается клиенту отдельно. */
export const orderAppliedAdvance = (context: AdvanceContext | null | undefined): number =>
  (context?.advance_total ?? 0) - (context?.overpayment_to_refund ?? 0);

/** Заказ идёт через ККТ, когда есть что оплатить на кассе или нужен итоговый чек зачёта аванса. */
export const orderSettlesThroughKkm = (
  context: Pick<OrderCheckoutContextEntity, 'fiscalization_required' | 'final_receipt_required'>,
  payableAmount: number,
): boolean => Boolean(context.fiscalization_required) && (payableAmount > 0 || Boolean(context.final_receipt_required));

type PaymentMethodAvailability = Readonly<{
  canUseCard: boolean;
  canUseCash: boolean;
  canUseSplit: boolean;
}>;

type SettlementBase = {
  attempt: OrderAttempt;
  payableAmount: number;
  loyaltyPoints?: number;
};

type CashSettlement = SettlementBase & {
  tenderedAmount: number;
};

type SplitSettlement = SettlementBase & {
  cashAmount: number;
};

type Settlement = SettlementBase & {
  mode: OrderPaymentMode;
  tenderedAmount?: number;
  cashAmount?: number;
};

type SettlementValidation = {
  mode: OrderPaymentMode;
  payableAmount: number;
  tenderedAmount?: number | null;
  cashAmount?: number | null;
};

const assertMoney = (amount: number, field: string): void => {
  if (!isMoney(amount)) {
    throw new OrderInputError(`${field} должен быть неотрицательной суммой в копейках`);
  }
};

const assertLoyaltyPoints = (points: number): void => {
  if (!Number.isSafeInteger(points) || points < 0) {
    throw new OrderInputError('Количество баллов должно быть неотрицательным целым числом');
  }
};

const assertPositivePayable = (payableAmount: number): void => {
  assertMoney(payableAmount, 'Сумма к оплате');
  if (payableAmount === 0) {
    throw new OrderInputError('При нулевой сумме используется settlement без оплат');
  }
};

const toInput = (attempt: OrderAttempt, payments: SettleOrderPaymentInput[], loyaltyPoints = 0): SettleOrderInput => {
  assertLoyaltyPoints(loyaltyPoints);

  return {
    orderId: attempt.orderId,
    expectedVersion: attempt.expectedVersion,
    idempotencyKey: attempt.idempotencyKey,
    payments,
    ...(loyaltyPoints > 0 ? { loyaltyPoints } : {}),
  };
};

export const clampLoyaltyPoints = (points: number, maximumPoints: number): number => {
  const safeMaximum = Number.isSafeInteger(maximumPoints) && maximumPoints > 0 ? maximumPoints : 0;
  const safePoints = Number.isFinite(points) ? Math.trunc(points) : 0;

  return Math.min(safeMaximum, Math.max(0, safePoints));
};

/**
 * Нормализует операторский ввод баллов до значения, которое реально спишется:
 * пустая строка остаётся пустой, всё остальное проходит через `clampLoyaltyPoints`.
 */
export const sanitizeLoyaltyPointsInput = (rawValue: string, maximumPoints: number): string =>
  rawValue.trim() === '' ? '' : String(clampLoyaltyPoints(Number(rawValue), maximumPoints));

export const calculatePayableAmount = (totalAmount: number, loyaltyPoints: number, advanceAmount = 0): number => {
  assertMoney(totalAmount, 'Итог');
  assertMoney(advanceAmount, 'Аванс');
  assertLoyaltyPoints(loyaltyPoints);

  const loyaltyAmount = loyaltyPoints * KOPECKS_PER_LOYALTY_POINT;
  if (!Number.isSafeInteger(loyaltyAmount) || loyaltyAmount > totalAmount) {
    throw new OrderInputError('Количество баллов превышает итоговую сумму');
  }

  return Math.max(0, totalAmount - loyaltyAmount - advanceAmount);
};

/** Аванс зачитывается не больше суммы после баллов, остаток аванса показывается переплатой. */
export const orderAdvanceBreakdown = (
  totalAmount: number,
  loyaltyPoints: number,
  context: AdvanceContext | null | undefined,
): { advanceAmount: number; overpaymentAmount: number; payableAmount: number } => {
  const advanceAmount = Math.min(orderAppliedAdvance(context), calculatePayableAmount(totalAmount, loyaltyPoints));

  return {
    advanceAmount,
    overpaymentAmount: (context?.advance_total ?? 0) - advanceAmount,
    payableAmount: calculatePayableAmount(totalAmount, loyaltyPoints, advanceAmount),
  };
};

export const resolvePaymentModeAfterMethodsChange = (
  currentMode: OrderPaymentMode,
  { canUseCard, canUseCash, canUseSplit }: PaymentMethodAvailability,
): OrderPaymentMode => {
  if (currentMode === 'card' && canUseCard) {
    return currentMode;
  }

  if (currentMode === 'cash' && canUseCash) {
    return currentMode;
  }

  if (currentMode === 'split' && canUseSplit && canUseCard && canUseCash) {
    return currentMode;
  }

  return null;
};

/** `null` означает, что полученной наличности недостаточно для подтверждения. */
export const calculateCashChange = (totalAmount: number, tenderedAmount: number): number | null => {
  assertMoney(totalAmount, 'Итог');
  assertMoney(tenderedAmount, 'Получено');

  return tenderedAmount >= totalAmount ? tenderedAmount - totalAmount : null;
};

export const buildCashSettlementInput = ({
  attempt,
  payableAmount,
  loyaltyPoints,
  tenderedAmount,
}: CashSettlement): SettleOrderInput => {
  assertPositivePayable(payableAmount);
  if (calculateCashChange(payableAmount, tenderedAmount) === null) {
    throw new OrderInputError('Полученная наличность меньше суммы к оплате');
  }

  return toInput(
    attempt,
    [
      {
        method: OrderPaymentMethod.Cash,
        // Ввод купюры нужен только калькулятору сдачи. API получает сумму,
        // которая остаётся в оплате после выдачи сдачи.
        amount: payableAmount,
      },
    ],
    loyaltyPoints,
  );
};

export const buildCardSettlementInput = ({
  attempt,
  payableAmount,
  loyaltyPoints,
}: SettlementBase): SettleOrderInput => {
  assertPositivePayable(payableAmount);

  return toInput(attempt, [{ method: OrderPaymentMethod.Card, amount: payableAmount }], loyaltyPoints);
};

export const buildSplitSettlementInput = ({
  attempt,
  payableAmount,
  loyaltyPoints,
  cashAmount,
}: SplitSettlement): SettleOrderInput => {
  assertPositivePayable(payableAmount);
  assertMoney(cashAmount, 'Наличные');

  if (cashAmount === 0 || cashAmount >= payableAmount) {
    throw new OrderInputError('Для разделённой оплаты наличные должны быть больше нуля и меньше итога');
  }

  const cardAmount = payableAmount - cashAmount;

  return toInput(
    attempt,
    [
      {
        method: OrderPaymentMethod.Cash,
        amount: cashAmount,
      },
      { method: OrderPaymentMethod.Card, amount: cardAmount },
    ],
    loyaltyPoints,
  );
};

export const buildFreeSettlementInput = (attempt: OrderAttempt, loyaltyPoints = 0): SettleOrderInput =>
  toInput(attempt, [], loyaltyPoints);

/**
 * Единственная точка выбора билдера по режиму оплаты чекаута.
 *
 * `-1` вместо отсутствующей суммы — намеренно: он гарантированно проваливает
 * `assertMoney`/`calculateCashChange` и даёт `OrderInputError` вместо молчаливой
 * оплаты на ноль.
 */
export const buildSettlementInput = ({
  attempt,
  payableAmount,
  loyaltyPoints,
  mode,
  tenderedAmount,
  cashAmount,
}: Settlement): SettleOrderInput => {
  if (payableAmount === 0) {
    return buildFreeSettlementInput(attempt, loyaltyPoints);
  }
  if (mode === 'link') throw new OrderInputError('Ссылка создаёт аванс. Зачтите его после получения оплаты.');
  if (mode === 'invoice') throw new OrderInputError('Заказ по счёту оплачивается поступлениями на расчётный счёт.');

  if (mode === 'cash') {
    return buildCashSettlementInput({ attempt, payableAmount, loyaltyPoints, tenderedAmount: tenderedAmount ?? -1 });
  }

  if (mode === 'split') {
    return buildSplitSettlementInput({ attempt, payableAmount, loyaltyPoints, cashAmount: cashAmount ?? -1 });
  }

  return buildCardSettlementInput({ attempt, payableAmount, loyaltyPoints });
};

/**
 * Предикат-близнец `buildSettlementInput`: отвечает на вопрос «этот ввод оплаты
 * доедет до билдера без `OrderInputError`». Состояние экрана (свежесть чека,
 * блокеры смены, submitting) сюда не входит — это правила ввода, а не экрана.
 */
export const isSettlementInputValid = (
  { mode, payableAmount, tenderedAmount, cashAmount }: SettlementValidation,
  { canUseCard, canUseCash, canUseSplit }: PaymentMethodAvailability,
): boolean => {
  if (payableAmount === 0) {
    return true;
  }

  if (mode === 'card') {
    return canUseCard;
  }

  if (mode === 'cash') {
    // `isMoney` до `calculateCashChange`: иначе `assertMoney` бросит на пустом вводе.
    return (
      canUseCash &&
      tenderedAmount != null &&
      isMoney(tenderedAmount) &&
      calculateCashChange(payableAmount, tenderedAmount) !== null
    );
  }

  if (mode === 'split') {
    return canUseSplit && cashAmount != null && isMoney(cashAmount) && cashAmount > 0 && cashAmount < payableAmount;
  }

  return false;
};
